type Primitive = number | string | boolean | symbol | undefined
type Serializable = Primitive | Serializable[] | { [key: string]: Serializable }

export type SnippetArgs = Serializable[]

export function formatSnippetArgs(
  args: SnippetArgs,
  options: { maxLength: number } = { maxLength: 32 },
): string {
  const result = args.map((arg) => formatSnippetValue(arg))

  // Remove unused arguments from the end of the array
  while (result.length > 0 && result[result.length - 1] === undefined) {
    result.pop()
  }

  const resultStringLength = result.join(", ").length

  // If arguments are short enough, return them in a single line
  if (resultStringLength <= Math.max(options.maxLength, 0)) {
    return result.join(", ")
  }

  // Otherwise, indent the arguments
  return `\n${indentString(result.join(",\n"))}\n`
}

function formatSnippetValue(
  value: Serializable | Record<string, Serializable>,
): string | undefined {
  if (value === undefined) {
    return undefined
  }

  if (Array.isArray(value)) {
    return formatSnippetArray(value)
  }

  switch (typeof value) {
    case "number":
    case "boolean":
      return value.toString()

    case "string":
      return `"${value}"`

    case "symbol":
      // Symbols are used to represent variables ("raw code") in the request snippet
      return value.description ?? String(value)

    case "object":
      return formatSnippetObject(value)

    default:
      throw new Error(`Unknown value type: ${typeof value}`)
  }
}

const formatSnippetObject = (
  object: Record<string, Serializable>,
): string | undefined => {
  const result = Object.entries(object)
    .map(([key, value]) => [key, formatSnippetValue(value)])
    .filter(([_, value]) => Boolean(value))
    .map(([key, value]) => `${key as string}: ${value as string}`)

  if (result.length === 0) {
    return undefined
  }

  return `{\n${indentString(result.join(",\n"))}\n}`
}

function formatSnippetArray(array: Serializable[]): string | undefined {
  if (array.length === 0) {
    return undefined
  }

  return `[${formatSnippetArgs(array)}]`
}

const indent = "  "

function indentString(string: string): string {
  return `${indent}${string.replace(/\n/g, `\n${indent}`)}`
}
